/**
 * Refresh the Supabase session before React Query refetches on focus (#1461).
 *
 * When a tab or the iOS app comes back after sleep, React Query refetches every
 * stale query at once. If the access token expired while hidden, that burst
 * (permissions, notifications, regions, tags, users…) went out with the stale
 * token and came back as ~10 × 401 before Supabase refreshed it — the bulk of
 * the gateway's 4xx on release day (9 Oct 2026).
 *
 * React Query's default focus listener reacts to `visibilitychange`. This one
 * does the same, but on becoming visible it first awaits
 * `supabase.auth.getSession()`, which refreshes an expired token, and only then
 * reports focus. Going hidden is reported immediately. If the session check
 * fails, focus is still reported, so a refetch is never lost.
 */

import { focusManager } from '@tanstack/react-query'

interface SessionSource {
  getSession: () => Promise<unknown>
}

export function installSessionAwareFocus(auth: SessionSource, doc: Document = document): void {
  focusManager.setEventListener((handleFocus) => {
    const onVisibilityChange = () => {
      if (doc.visibilityState !== 'visible') {
        handleFocus(false)
        return
      }
      auth.getSession().then(
        () => handleFocus(true),
        () => handleFocus(true),
      )
    }
    doc.addEventListener('visibilitychange', onVisibilityChange, false)
    return () => doc.removeEventListener('visibilitychange', onVisibilityChange)
  })
}
